import { useCallback, useState } from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useWriteOffMutation } from '../../store/api';
import { serverReasonOf } from '../balance-table/serverReason';

import {
  type HeldCharge,
  UNKNOWN_OUTCOME_TEXT,
  writeOffOutcomeText,
  type WriteOffReason,
  writeOffRequestOf,
} from './heldCharges';
import type { HeldChargeSurface } from './useBillHeldCharge';
import type { WriteOffHeldChargeDialogProps } from './WriteOffHeldChargeDialog';

interface Options {
  patientId: string;
  surface: HeldChargeSurface;
}

export interface WriteOffHeldCharge {
  openWriteOff: (charge: HeldCharge) => void;
  /** The charge whose write-off is in flight. */
  pendingChargeLineId: string | null;
  /** Everything the dialog renders; spread it onto WriteOffHeldChargeDialog. */
  dialog: WriteOffHeldChargeDialogProps;
}

/**
 * Write off on a charge held after a refund (gdn-4242), through the existing
 * write-offs route. The amount is fixed at what the charge owes; the route
 * requires a reason, so the dialog asks for one. The write-off's cache tags
 * refresh every surface that shows the charge.
 */
export function useWriteOffHeldCharge({ patientId, surface }: Options): WriteOffHeldCharge {
  const [writeOff] = useWriteOffMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { singular } = useSubjectLanguageVariants();
  const { billing } = useEventTracking();
  const [charge, setCharge] = useState<HeldCharge | null>(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<WriteOffReason | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pendingChargeLineId, setPendingChargeLineId] = useState<string | null>(null);

  const eventOf = useCallback(
    (target: HeldCharge) => ({
      patient_id: patientId,
      charge_line_id: target.chargeLineId,
      amount_cents: target.amountCents,
      surface,
    }),
    [patientId, surface],
  );

  const openWriteOff = useCallback(
    (target: HeldCharge) => {
      billing.patientHeldChargeWriteOffOpened(eventOf(target));
      setCharge(target);
      setReason(null);
      setNote('');
      setError(null);
      setOpen(true);
    },
    [billing, eventOf],
  );

  const submitting = open && pendingChargeLineId !== null;

  const onCancel = useCallback(() => {
    if (!submitting) setOpen(false);
  }, [submitting]);

  const onConfirm = useCallback(async () => {
    if (!charge || !reason) return;
    billing.patientHeldChargeWriteOffSubmitted({ ...eventOf(charge), reason });
    setError(null);
    setPendingChargeLineId(charge.chargeLineId);
    try {
      await writeOff({
        patientId,
        writeOffRequest: writeOffRequestOf(charge, reason, note),
      }).unwrap();
      enqueueSnackbar(writeOffOutcomeText(charge), { variant: 'success' });
      setOpen(false);
    } catch (failure) {
      setError(serverReasonOf(failure) ?? UNKNOWN_OUTCOME_TEXT);
    } finally {
      setPendingChargeLineId(null);
    }
  }, [billing, charge, enqueueSnackbar, eventOf, note, patientId, reason, writeOff]);

  return {
    openWriteOff,
    pendingChargeLineId,
    dialog: {
      open,
      charge,
      subject: singular,
      reason,
      note,
      error,
      submitting,
      onReasonChange: setReason,
      onNoteChange: setNote,
      onCancel,
      onConfirm: () => void onConfirm(),
    },
  };
}
